import { Component, ComponentFactoryResolver, Injector } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { firstValueFrom, Subscription } from 'rxjs';
import { MODAL_OPTIONS } from './ngx-config';

const BASE_PROVIDERS = [ActivatedRoute, Router];

const NOT_FOUND = {};

/**
 * This container is used in the Router to host a modal component.
 *
 * The initial props/Inputs on a component can be set either statically using
 * setting an `initialState` value on the route's `data` object, and/or by extracting
 * the params from the query or the route by setting a `initialState` on the route's `resolve` object
 * to either {@link FullParamMapResolver} or {@link ParamMapResolver}.
 *
 * Resolving the `initialState` is the responsibility of the resolver that is passed in,
 * but defaults to the one provided in `data`.
 */
@Component({
    template: ``
})
export class ModalContainer {
    private modal: BsModalRef;
    private subscription: Subscription;

    constructor(
        private modalService: BsModalService,
        private router: Router,
        private injector: Injector,
        private activatedRoute: ActivatedRoute
    ) {}

    async ngOnInit() {
        const data = await firstValueFrom(this.activatedRoute.data);
        const modalProviders = data.modalProviders;

        const compFactoryResolver = this.injector.get(ComponentFactoryResolver);
        const compFactory = compFactoryResolver.resolveComponentFactory(
            data.modalContent
        );

        const initialState =
            data.initialState == null
                ? {}
                : compFactory.inputs.reduce(
                      (acc, prop) => ({
                          ...acc,
                          [prop.propName]: data.initialState[prop.propName]
                      }),
                      {}
                  );

        const providers = BASE_PROVIDERS.concat(modalProviders ?? []);

        this.modal = this.modalService.show(data.modalContent, {
            ...MODAL_OPTIONS,
            initialState,
            class: data.modalClass,
            providers: providers
                .map(p => ({
                    provide: p,
                    useValue: this.injector.get(p, NOT_FOUND)
                }))
                .filter(p => p.useValue !== NOT_FOUND)
        });

        this.subscription = this.modal.onHide.subscribe(() => {
            if (data.parentRoute) {
                this.router.navigate([data.parentRoute], {
                    queryParamsHandling: 'preserve'
                });
            } else if (data.relativeRoute) {
                this.router.navigate([data.relativeRoute], {
                    relativeTo: this.activatedRoute,
                    queryParamsHandling: 'preserve'
                });
            } else {
                this.router.navigate(['../'], {
                    relativeTo: this.activatedRoute,
                    queryParamsHandling: 'preserve'
                });
            }
        });
    }

    ngOnDestroy() {
        this.subscription.unsubscribe();
        this.modal.hide();
    }
}
